import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

export class SimplePDFGenerator {
  static async generatePDF(htmlContent: string, filename: string): Promise<void> {
    console.log(`🎯 Generating PDF: ${filename}`);

    // Check if the content has multiple pages (using .page class)
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlContent;
    const pages = tempContainer.querySelectorAll('.page');

    if (pages.length > 0) {
      // Multi-page PDF generation
      await this.generateMultiPagePDF(htmlContent, filename);
    } else {
      // Single page PDF generation (existing functionality)
      await this.generateSinglePagePDF(htmlContent, filename);
    }
  }

  private static async generateSinglePagePDF(htmlContent: string, filename: string): Promise<void> {
    // Create a temporary container
    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    container.style.width = '800px';
    container.style.padding = '40px';
    container.style.backgroundColor = 'white';
    container.style.fontFamily = 'DejaVu Sans, Arial, sans-serif';
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.zIndex = '10000';
    container.style.visibility = 'visible';
    document.body.appendChild(container);

    try {
      // Wait a bit for rendering
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(container, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true,
        useCORS: true
      });

      document.body.removeChild(container);

      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Canvas rendering failed');
      }

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.9);
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
      pdf.save(filename);

      console.log(`✅ PDF saved: ${filename}`);

    } catch (error) {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
      console.error('❌ Error generating PDF:', error);
      throw error;
    }
  }

  private static async generateMultiPagePDF(htmlContent: string, filename: string): Promise<void> {
    console.log(`🎯 Generating multi-page PDF: ${filename}`);

    // Create a container for all pages
    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    container.style.width = '800px';
    container.style.backgroundColor = 'white';
    container.style.fontFamily = 'DejaVu Sans, Arial, sans-serif';
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.zIndex = '10000';
    container.style.visibility = 'visible';
    document.body.appendChild(container);

    try {
      // Wait a bit for rendering
      await new Promise(resolve => setTimeout(resolve, 100));

      // Get all page elements
      const pageElements = container.querySelectorAll('.page');

      if (pageElements.length === 0) {
        throw new Error('No page elements found');
      }

      // Create PDF
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      // Process each page
      for (let i = 0; i < pageElements.length; i++) {
        const pageElement = pageElements[i] as HTMLElement;

        // Clone the page to avoid affecting the original
        const pageClone = pageElement.cloneNode(true) as HTMLElement;
        pageClone.style.width = '800px';
        pageClone.style.padding = '18mm 16mm 16mm';
        pageClone.style.margin = '0';
        pageClone.style.backgroundColor = 'white';
        pageClone.style.display = 'block';

        // Clear container and add only this page
        container.innerHTML = '';
        container.appendChild(pageClone);

        // Wait for rendering
        await new Promise(resolve => setTimeout(resolve, 50));

        // Render page to canvas
        const canvas = await html2canvas(container, {
          scale: 2,
          backgroundColor: '#ffffff',
          logging: false,
          allowTaint: true,
          useCORS: true
        });

        if (canvas.width === 0 || canvas.height === 0) {
          throw new Error(`Canvas rendering failed for page ${i + 1}`);
        }

        const imgData = canvas.toDataURL('image/jpeg', 0.9);
        const imgWidth = pdf.internal.pageSize.getWidth();
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (i > 0) {
          pdf.addPage();
        }

        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
      }

      pdf.save(filename);
      console.log(`✅ Multi-page PDF saved: ${filename}`);

    } catch (error) {
      console.error('❌ Error generating multi-page PDF:', error);
      throw error;
    } finally {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }
  }

  static async generatePDFBlob(htmlContent: string): Promise<Blob> {
    console.log(`🎯 Generating PDF blob`);

    // Check if the content has multiple pages
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlContent;
    const pages = tempContainer.querySelectorAll('.page');

    if (pages.length > 0) {
      return this.generateMultiPagePDFBlob(htmlContent);
    } else {
      return this.generateSinglePagePDFBlob(htmlContent);
    }
  }

  private static async generateSinglePagePDFBlob(htmlContent: string): Promise<Blob> {
    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    container.style.width = '800px';
    container.style.padding = '40px';
    container.style.backgroundColor = 'white';
    container.style.fontFamily = 'DejaVu Sans, Arial, sans-serif';
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.zIndex = '10000';
    container.style.visibility = 'visible';
    document.body.appendChild(container);

    try {
      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(container, {
        scale: 1.5,
        backgroundColor: '#ffffff',
        logging: false,
        allowTaint: true,
        useCORS: true
      });

      document.body.removeChild(container);

      if (canvas.width === 0 || canvas.height === 0) {
        throw new Error('Canvas rendering failed');
      }

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.8);
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);

      return pdf.output('blob');

    } catch (error) {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
      console.error('❌ Error generating PDF blob:', error);
      throw error;
    }
  }

  private static async generateMultiPagePDFBlob(htmlContent: string): Promise<Blob> {
    console.log(`🎯 Generating multi-page PDF blob`);

    const container = document.createElement('div');
    container.innerHTML = htmlContent;
    container.style.width = '800px';
    container.style.backgroundColor = 'white';
    container.style.fontFamily = 'DejaVu Sans, Arial, sans-serif';
    container.style.position = 'fixed';
    container.style.top = '0';
    container.style.left = '0';
    container.style.zIndex = '10000';
    container.style.visibility = 'visible';
    document.body.appendChild(container);

    try {
      await new Promise(resolve => setTimeout(resolve, 100));

      const pageElements = container.querySelectorAll('.page');

      if (pageElements.length === 0) {
        throw new Error('No page elements found');
      }

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });

      for (let i = 0; i < pageElements.length; i++) {
        const pageElement = pageElements[i] as HTMLElement;

        const pageClone = pageElement.cloneNode(true) as HTMLElement;
        pageClone.style.width = '800px';
        pageClone.style.padding = '18mm 16mm 16mm';
        pageClone.style.margin = '0';
        pageClone.style.backgroundColor = 'white';
        pageClone.style.display = 'block';

        container.innerHTML = '';
        container.appendChild(pageClone);

        await new Promise(resolve => setTimeout(resolve, 50));

        const canvas = await html2canvas(container, {
          scale: 1.5,
          backgroundColor: '#ffffff',
          logging: false,
          allowTaint: true,
          useCORS: true
        });

        if (canvas.width === 0 || canvas.height === 0) {
          throw new Error(`Canvas rendering failed for page ${i + 1}`);
        }

        const imgData = canvas.toDataURL('image/jpeg', 0.8);
        const imgWidth = pdf.internal.pageSize.getWidth();
        const imgHeight = (canvas.height * imgWidth) / canvas.width;

        if (i > 0) {
          pdf.addPage();
        }

        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
      }

      return pdf.output('blob');

    } catch (error) {
      console.error('❌ Error generating multi-page PDF blob:', error);
      throw error;
    } finally {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    }
  }
}
